'use client';

import { ChevronsUpDownIcon, XIcon, CheckIcon } from 'lucide-react';
import { cn } from '@root/lib/utils';
import { Button } from '@root/components/v3/button';
import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@root/components/v3/popover';
import { Input } from '@root/components/v3/input';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { Badge } from '@root/components/v3/badge';

type MultiSelectContextType = {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedValues: Set<string>;
  toggleValue: (value: string) => void;
  items: Map<string, ReactNode>;
  onItemAdded: (value: string, label: ReactNode) => void;
};

const MultiSelectContext = createContext<MultiSelectContextType | null>(null);

export function MultiSelect({
  children,
  values,
  defaultValues,
  onValuesChange,
}: {
  children: ReactNode;
  values?: string[];
  defaultValues?: string[];
  onValuesChange?: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [internalValues, setInternalValues] = useState(new Set<string>(values ?? defaultValues));
  const selectedValues = values ? new Set(values) : internalValues;
  const [items, setItems] = useState<Map<string, ReactNode>>(new Map());

  function toggleValue(value: string) {
    const getNewSet = (prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    };
    setInternalValues(getNewSet);
    onValuesChange?.([...getNewSet(selectedValues)]);
  }

  const onItemAdded = useCallback((value: string, label: ReactNode) => {
    setItems(prev => {
      if (prev.get(value) === label) return prev;
      return new Map(prev).set(value, label);
    });
  }, []);

  return (
    <MultiSelectContext.Provider
      value={{
        open,
        setOpen,
        selectedValues,
        toggleValue,
        items,
        onItemAdded,
      }}>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        {children}
      </Popover>
    </MultiSelectContext.Provider>
  );
}

export function MultiSelectTrigger({
  className,
  children,
  ...props
}: {
  className?: string;
  children?: ReactNode;
} & ComponentPropsWithoutRef<typeof Button>) {
  const { open } = useMultiSelectContext();

  return (
    <PopoverTrigger asChild>
      <Button
        {...props}
        variant={props.variant ?? 'outline'}
        role={props.role ?? 'combobox'}
        aria-expanded={props['aria-expanded'] ?? open}
        className={cn(
          'flex h-11  min-h-9 px-3 py-2 w-fit items-center justify-between gap-2 overflow-hidden rounded-[8px] border border-input bg-transparent text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}>
        {children}
        <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
  );
}

export function MultiSelectValue({
  placeholder,
  clickToRemove = true,
  className,
  overflowBehavior = 'wrap-when-open',
  ...props
}: {
  placeholder?: string;
  clickToRemove?: boolean;
  overflowBehavior?: 'wrap' | 'wrap-when-open' | 'cutoff';
} & Omit<ComponentPropsWithoutRef<'div'>, 'children'>) {
  const { selectedValues, toggleValue, items, open } = useMultiSelectContext();
  const [overflowAmount, setOverflowAmount] = useState(0);
  const valueRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);

  const shouldWrap = overflowBehavior === 'wrap' || (overflowBehavior === 'wrap-when-open' && open);

  const checkOverflow = useCallback(() => {
    if (valueRef.current == null) return;

    const containerElement = valueRef.current;
    const overflowElement = overflowRef.current;
    const items = containerElement.querySelectorAll<HTMLElement>('[data-selected-item]');

    if (overflowElement != null) overflowElement.style.display = 'none';
    items.forEach(child => child.style.removeProperty('display'));
    let amount = 0;
    for (let i = items.length - 1; i >= 0; i--) {
      const child = items[i]!;
      if (containerElement.scrollWidth <= containerElement.clientWidth) {
        break;
      }
      amount = items.length - i;
      child.style.display = 'none';
      overflowElement?.style.removeProperty('display');
    }
    setOverflowAmount(amount);
  }, []);

  const handleResize = useCallback(
    (node: HTMLDivElement | null) => {
      // Guard: If node is null, just clean up and return
      if (!node) {
        valueRef.current = null;
        return;
      }

      valueRef.current = node;

      const mutationObserver = new MutationObserver(checkOverflow);
      const observer = new ResizeObserver(debounce(checkOverflow, 100));

      mutationObserver.observe(node, {
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
      observer.observe(node);

      return () => {
        observer.disconnect();
        mutationObserver.disconnect();
        valueRef.current = null;
      };
    },
    [checkOverflow],
  );

  if (selectedValues.size === 0 && placeholder) {
    return <span className="min-w-0 overflow-hidden font-normal text-muted-foreground">{placeholder}</span>;
  }

  return (
    <div
      {...props}
      ref={handleResize}
      className={cn('flex w-full  gap-1.5 overflow-hidden', shouldWrap && 'h-full flex-wrap', className)}>
      {[...selectedValues].map(value => (
          <Badge
            variant="outline"
            data-selected-item
            className="group flex items-center gap-1"
            key={value}
            onClick={
              clickToRemove
                ? e => {
                    e.stopPropagation();
                    toggleValue(value);
                  }
                : undefined
            }>
            {items.get(value) ?? value}
            {clickToRemove && <XIcon className="size-2 text-muted-foreground group-hover:text-destructive" />}
          </Badge>
        ))}
      <Badge
        style={{
          display: overflowAmount > 0 && !shouldWrap ? 'block' : 'none',
        }}
        variant="outline"
        ref={overflowRef}>
        +{overflowAmount}
      </Badge>
    </div>
  );
}

export function MultiSelectContent({
  search = true,
  children,
  className,
}: {
  search?: boolean | { placeholder?: string; emptyMessage?: string };
  children: ReactNode;
  className?: string;
}) {
  const [searchValue, setSearchValue] = useState('');
  const canSearch = typeof search === 'object' ? true : search;
  const searchPlaceholder = typeof search === 'object' ? search.placeholder : 'Search...';
  const emptyMessage = typeof search === 'object' ? search.emptyMessage : 'No results found.';

  // Filter children based on search
  const filteredChildren = React.useMemo(() => {
    if (!canSearch || !searchValue) return children;

    const searchLower = searchValue.toLowerCase();
    return React.Children.toArray(children).filter(child => {
      if (!React.isValidElement(child)) return true;

      // Get the text content from the child
      const childText = getChildText(child);
      return childText.toLowerCase().includes(searchLower);
    });
  }, [children, searchValue, canSearch]);

  const hasResults = React.Children.count(filteredChildren) > 0;

  return (
    <PopoverContent className={cn('min-w-[var(--radix-popover-trigger-width)] p-0', className)}>
      <div className="flex flex-col">
        {canSearch && (
          <div className="border-b px-3 py-2">
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              className="h-8"
            />
          </div>
        )}
        <div className="max-h-64 overflow-y-auto p-1">
          {hasResults ? (
            filteredChildren
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
          )}
        </div>
      </div>
    </PopoverContent>
  );
}

export function MultiSelectItem({
  value,
  children,
  badgeLabel,
  onSelect,
  className,
  disabled,
  ...props
}: {
  badgeLabel?: ReactNode;
  value: string;
  className?: string;
  onSelect?: (value: string) => void;
  disabled?: boolean;
} & Omit<ComponentPropsWithoutRef<'div'>, 'value' | 'onSelect'>) {
  const { toggleValue, selectedValues, onItemAdded } = useMultiSelectContext();
  const isSelected = selectedValues.has(value);

  useEffect(() => {
    onItemAdded(value, badgeLabel ?? children);
  }, [value, children, onItemAdded, badgeLabel]);

  return (
    <div
      {...props}
      onClick={() => {
        if (disabled) return;
        toggleValue(value);
        onSelect?.(value);
      }}
      className={cn(
        'flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
        isSelected && 'bg-accent',
        disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent hover:text-inherit',
        className,
      )}>
      <CheckIcon className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
      {children}
    </div>
  );
}

// Helper function to extract text from React children
function getChildText(child: React.ReactElement): string {
  if (typeof child === 'string') return child;
  if (typeof child === 'number') return child.toString()!;

  const props = child.props;
  if (!props) return '';

  // Check for value prop (common in select items)
  if (props.value && typeof props.value === 'string') return props.value;

  // Check for children
  if (props.children) {
    if (typeof props.children === 'string') return props.children;
    if (typeof props.children === 'number') return props.children.toString();
    if (Array.isArray(props.children)) {
      return props.children.map(c => (typeof c === 'string' || typeof c === 'number' ? c.toString() : '')).join(' ');
    }
  }

  return '';
}

function useMultiSelectContext() {
  const context = useContext(MultiSelectContext);
  if (context == null) {
    throw new Error('useMultiSelectContext must be used within a MultiSelectContext');
  }
  return context;
}

function debounce<T extends (...args: never[]) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function (this: unknown, ...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}
export function MultiSelectInfiniteContent({
  children,
  search = true,
  onSearchChange,
  searchValue: externalSearchValue,
  className,
}: {
  children: ReactNode;
  search?: boolean | { placeholder?: string };
  onSearchChange?: (value: string) => void;
  searchValue?: string;
  className?: string;
}) {
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const searchValue = externalSearchValue ?? internalSearchValue;
  const canSearch = typeof search === 'object' ? true : search;
  const searchPlaceholder = typeof search === 'object' ? search.placeholder : 'Search...';

  const handleSearchChange = (value: string) => {
    setInternalSearchValue(value);
    onSearchChange?.(value);
  };

  return (
    <PopoverContent className={cn('min-w-[var(--radix-popover-trigger-width)] p-0', className)}>
      <div className="flex flex-col">
        {canSearch && (
          <div className="border-b px-3 py-2">
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={e => handleSearchChange(e.target.value)}
              className="h-8"
            />
          </div>
        )}
        <div className="max-h-64 overflow-y-auto" id="multi-select-infinite-scroll">
          {children}
        </div>
      </div>
    </PopoverContent>
  );
}
